/** CMS-editable About Us content for /about-us */
export const DEFAULT_ABOUT_CMS_CONFIG = {
  schemaVersion: 2,
  heroEyebrow: "ABOUT US",
  heroTitleLine1: "Connecting Businesses.",
  heroTitleLine2: "Creating Opportunities.",
  heroLead:
    "Trocanet is a trusted B2B marketplace platform that connects buyers, sellers, and service providers across industries, enabling smarter business and sustainable growth.",
  heroCtaLabel: "Join Our Network",
  heroCtaLink: "/signup",
  heroBackgroundUrl:
    "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=80",
  heroCityOverlayUrl:
    "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1400&q=80",
  whoEyebrow: "WHO WE ARE",
  whoTitle: "About Trocanet",
  whoParagraph1:
    "Trocanet is a unified B2B platform designed to simplify the way businesses connect, collaborate, and grow. We bring together buyers, suppliers, manufacturers, and service providers in one digital ecosystem to drive transparency, efficiency, and trust.",
  whoParagraph2: "",
  whoCtaLabel: "Learn More About Our Mission",
  whoCtaLink: "#hub-about-offers",
  whoCards: [
    {
      key: "vision",
      title: "Our Vision",
      desc: "To be the most trusted global B2B marketplace that drives business growth and innovation.",
    },
    {
      key: "mission",
      title: "Our Mission",
      desc: "To empower businesses by providing a seamless, reliable, and efficient digital platform.",
    },
    {
      key: "values",
      title: "Our Values",
      desc: "Integrity, transparency, customer focus, innovation, and collaboration drive everything we do.",
    },
    {
      key: "commitment",
      title: "Our Commitment",
      desc: "We are committed to delivering value, building trust, and creating long-term partnerships.",
    },
  ],
};

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function mergeWhoCards(defaults, incoming) {
  const source = Array.isArray(incoming) && incoming.length ? incoming : defaults;
  return source.map((item, index) => {
    const base = defaults[index] || { key: `card-${index}`, title: "", desc: "" };
    const row = isPlainObject(item) ? item : {};
    return {
      key: String(row.key || base.key || `card-${index}`).trim(),
      title: String(row.title ?? base.title ?? "").trim(),
      desc: String(row.desc ?? base.desc ?? "").trim(),
    };
  });
}

export function parseAboutCmsConfig(content) {
  if (!content || typeof content !== "string") {
    return { ...DEFAULT_ABOUT_CMS_CONFIG };
  }

  const trimmed = content.trim();
  if (!trimmed.startsWith("{")) {
    return { ...DEFAULT_ABOUT_CMS_CONFIG };
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (!isPlainObject(parsed)) {
      return { ...DEFAULT_ABOUT_CMS_CONFIG };
    }

    const defaults = DEFAULT_ABOUT_CMS_CONFIG;
    return {
      ...defaults,
      heroEyebrow: String(parsed.heroEyebrow ?? defaults.heroEyebrow).trim(),
      heroTitleLine1: String(parsed.heroTitleLine1 ?? defaults.heroTitleLine1).trim(),
      heroTitleLine2: String(parsed.heroTitleLine2 ?? defaults.heroTitleLine2).trim(),
      heroLead: String(parsed.heroLead ?? defaults.heroLead).trim(),
      heroCtaLabel: String(parsed.heroCtaLabel ?? defaults.heroCtaLabel).trim(),
      heroCtaLink: String(parsed.heroCtaLink ?? defaults.heroCtaLink).trim() || "/signup",
      heroBackgroundUrl: String(parsed.heroBackgroundUrl ?? defaults.heroBackgroundUrl).trim(),
      heroCityOverlayUrl: String(parsed.heroCityOverlayUrl ?? defaults.heroCityOverlayUrl).trim(),
      whoEyebrow: String(parsed.whoEyebrow ?? defaults.whoEyebrow).trim(),
      whoTitle: String(parsed.whoTitle ?? defaults.whoTitle).trim(),
      whoParagraph1: String(parsed.whoParagraph1 ?? defaults.whoParagraph1).trim(),
      whoParagraph2: String(parsed.whoParagraph2 ?? defaults.whoParagraph2).trim(),
      whoCtaLabel: String(parsed.whoCtaLabel ?? defaults.whoCtaLabel).trim(),
      whoCtaLink: String(parsed.whoCtaLink ?? defaults.whoCtaLink).trim() || "#hub-about-offers",
      whoCards: mergeWhoCards(defaults.whoCards, parsed.whoCards),
    };
  } catch {
    return { ...DEFAULT_ABOUT_CMS_CONFIG };
  }
}

export function serializeAboutCmsConfig(config) {
  const data = config || DEFAULT_ABOUT_CMS_CONFIG;
  return JSON.stringify(
    {
      schemaVersion: 2,
      heroEyebrow: data.heroEyebrow,
      heroTitleLine1: data.heroTitleLine1,
      heroTitleLine2: data.heroTitleLine2,
      heroLead: data.heroLead,
      heroCtaLabel: data.heroCtaLabel,
      heroCtaLink: data.heroCtaLink,
      heroBackgroundUrl: data.heroBackgroundUrl,
      heroCityOverlayUrl: data.heroCityOverlayUrl,
      whoEyebrow: data.whoEyebrow,
      whoTitle: data.whoTitle,
      whoParagraph1: data.whoParagraph1,
      whoParagraph2: data.whoParagraph2,
      whoCtaLabel: data.whoCtaLabel,
      whoCtaLink: data.whoCtaLink,
      whoCards: data.whoCards,
    },
    null,
    2,
  );
}

export function buildAboutCmsSections(cmsPage) {
  return parseAboutCmsConfig(cmsPage?.content);
}

export function aboutConfigFromCmsRecord(record) {
  return parseAboutCmsConfig(record?.content);
}

export function aboutCmsPayloadFromConfig(config) {
  const data = config || DEFAULT_ABOUT_CMS_CONFIG;
  return {
    title: "About Us",
    description: (data.whoParagraph1 || data.heroLead || "").slice(0, 200),
    content: serializeAboutCmsConfig(data),
  };
}
